import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2, AlertTriangle, Bell, BellOff } from 'lucide-react';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * Los avisos que esta cápsula le manda a quien pidió el caso.
 *
 * **De dónde sale.** El 20 de agosto Marcelo lo dijo así: *"atendió el bot y después de nuestro
 * lado se durmió una semana"*. Hasta hoy el solicitante no recibía un solo aviso.
 *
 * **Por qué el disparador se dice en prosa y no con el nombre de un estado.** Lo que se declara
 * es una bandera del ciclo de vida —*el caso entra a un estado que detiene el reloj*— y por eso
 * el mismo aviso sirve en una cápsula que llama `resolved` a lo que otra llama `finalizado`. La
 * pantalla muestra, al lado, **cuáles son hoy esos estados en esta cápsula**, que es lo que hace
 * que la abstracción no sea un acertijo.
 *
 * **Prender un aviso le escribe a personas reales**, así que se confirma. Apagarlo no: dejar de
 * mandar nunca necesitó permiso.
 */
const VARIABLES = [
  ['{{ticket_id}}', 'el número del caso'],
  ['{{titulo}}', 'el título'],
  ['{{solicitante}}', 'quién lo pidió'],
  ['{{propietario}}', 'quién lo atiende'],
  ['{{estado}}', 'el estado actual'],
  ['{{compromiso}}', 'la fecha comprometida, ya redactada'],
  ['{{link_conformidad}}', 'el enlace a la encuesta — sólo si está prendida arriba'],
];

// **Sólo existen en los avisos de una autorización.** Nombrarlas en un aviso del caso las deja
// escritas como `{{autorizante}}` en el mail de una persona: no hay ninguna autorización de la
// que hablar, así que nadie las llena.
const VARIABLES_DE_AUTORIZACION = [
  ['{{autorizacion}}', 'el título de la autorización'],
  ['{{autorizante}}', 'quién tiene que autorizar, o quién decidió'],
  ['{{resultado}}', 'aprobada, rechazada o pendiente'],
  ['{{motivo_de_la_decision}}', 'el comentario, ya redactado — vacío si no dejó ninguno'],
];

const A_QUIEN = {
  solicitante: 'a quien pidió el caso',
  propietario: 'a quien lo tiene',
  rol: 'a todos los del rol',
  jefe: 'al jefe de quien lo tiene',
  jefe_del_solicitante: 'al jefe de quien lo pidió',
  asignado: 'a quien tiene que autorizar',
  personas: 'a las personas de la lista',
};

// Los disparadores que le pasan a **una autorización** y no al caso. Deciden qué variables
// tienen sentido y si hay una lista de personas que editar.
const DE_UNA_AUTORIZACION = new Set([
  'al_pedir_autorizacion', 'al_aprobar', 'al_rechazar', 'al_cancelarse_por_rechazo',
]);

// **Cada bandera de la condición, con cómo se lee en sí y en no, y de qué campo del estado sale
// (2026-10-09).** La versión anterior tenía dos errores que hacían mentir a la pantalla:
//
// - buscaba cada bandera **con su propio nombre** en el catálogo de estados, y dos no se llaman
//   igual: `esta_abierto` se declara como `cuenta_como_abierto`, y `cuenta_para_sla` es lo
//   contrario de `excluye_del_sla`. Un aviso condicionado en «abierto» no encontraba ningún
//   estado;
// - y **descartaba las condiciones en `false`**. El aviso de cierre al solicitante pide
//   `{esta_abierto: false, exige_conformidad: false}`, así que se leía «sin condición» y, debajo,
//   *«ningún estado cumple la condición — el aviso no saldría nunca»*, mientras estaba prendido y
//   salía.
//
// Es la traducción de `services/ciclo_de_vida.py` y tiene que decir lo mismo que esas funciones.
// Un test del backend verifica que cada bandera de `catalogo_de_avisos.BANDERAS` esté acá.
const BANDERAS = {
  esta_abierto: {
    si: 'que deja el caso abierto', no: 'que cierra el caso',
    declara: (s) => s?.cuenta_como_abierto !== false,
  },
  detiene_el_reloj: {
    si: 'que detiene el reloj', no: 'con el reloj corriendo',
    declara: (s) => !!s?.detiene_el_reloj,
  },
  implica_atendido: {
    si: 'que implica atención', no: 'que no implica atención',
    declara: (s) => !!s?.implica_atendido,
  },
  cuenta_para_sla: {
    si: 'que cuenta para el SLA', no: 'que no cuenta para el SLA',
    declara: (s) => !s?.excluye_del_sla,
  },
  exige_conformidad: {
    si: 'que exige la calificación de quien pidió', no: 'que no espera calificación',
    declara: (s) => !!s?.exige_conformidad,
  },
};

function condiciones(aviso) {
  const c = aviso?.cuando;
  return Object.entries((typeof c === 'object' && c?.banderas) || {});
}

function cuandoEnProsa(aviso) {
  const c = aviso?.cuando;
  const tipo = typeof c === 'object' ? c?.tipo : c;
  if (tipo === 'al_tomar_propiedad') return 'cuando alguien pasa a ser responsable del caso';
  if (tipo === 'al_pedir_autorizacion') return 'cuando se pide una autorización sobre el caso';
  if (tipo === 'al_aprobar') return 'cuando alguien autoriza';
  if (tipo === 'al_rechazar') return 'cuando alguien no autoriza';
  if (tipo === 'al_cancelarse_por_rechazo') return 'cuando una autorización deja de hacer falta porque otra se rechazó';
  if (tipo === 'al_no_contestar_la_conformidad') return 'cuando pasan los días y quien pidió no calificó';
  if (tipo === 'al_entrar_a_estado') {
    const partes = condiciones(aviso).map(([k, v]) => (BANDERAS[k] ? BANDERAS[k][v ? 'si' : 'no'] : k));
    return `cuando el caso entra a un estado ${partes.join(' y ') || '(sin condición)'}`;
  }
  return tipo || 'sin disparador';
}

/** Los estados de esta cápsula que hoy cumplen la condición. Se leen del catálogo, no se deducen. */
function estadosQueCumplen(aviso, ticketStatuses) {
  const c = aviso?.cuando;
  if ((typeof c === 'object' ? c?.tipo : c) !== 'al_entrar_a_estado') return null;
  const conds = condiciones(aviso).filter(([b]) => BANDERAS[b]);
  if (!conds.length) return [];
  return (ticketStatuses || [])
    .filter((s) => conds.every(([b, v]) => BANDERAS[b].declara(s) === !!v))
    .map((s) => s.id);
}

export default function AvisosDeLaCapsula({ config, handleSave, saving }) {
  const [borrador, setBorrador] = useState([]);
  const [confirmar, setConfirmar] = useState({ open: false });

  useEffect(() => { setBorrador(config?.avisos || []); }, [config?.avisos]);

  const sucio = useMemo(
    () => JSON.stringify(borrador) !== JSON.stringify(config?.avisos || []),
    [borrador, config?.avisos],
  );
  const prendidos = borrador.filter((a) => a.activo).length;

  const cambiar = (id, campo, valor) =>
    setBorrador((prev) => prev.map((a) => (a.id === id ? { ...a, [campo]: valor } : a)));

  const alternar = (aviso) => {
    if (!aviso.activo) {
      setConfirmar({
        open: true,
        title: 'Prender este aviso',
        description:
          `A partir de que guardes, este aviso le va a llegar de verdad ${A_QUIEN[aviso.a_quien] || ''} ` +
          `${cuandoEnProsa(aviso)}. Leé el texto antes: es el que va a recibir una persona.`,
        confirmText: 'Prenderlo',
        onConfirm: () => cambiar(aviso.id, 'activo', true),
      });
      return;
    }
    cambiar(aviso.id, 'activo', false);
  };

  if (!borrador.length) {
    return (
      <Card className="rounded-sm border-brand-border" data-testid="avisos-vacio">
        <CardContent className="p-6 text-xs text-zinc-500">
          Esta cápsula todavía no tiene catálogo de avisos. Se siembra al crearla; para una
          cápsula anterior lo deja <code className="bg-zinc-100 px-1">sembrar_avisos_de_la_capsula.py</code>.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="avisos-de-la-capsula">
      <ConfirmActionDialog
        {...confirmar}
        onOpenChange={(open) => setConfirmar((p) => ({ ...p, open }))}
      />

      <QueSeConfiguraAca
        testId="ayuda-avisos"
        queEs="Los mensajes que esta cápsula le manda a alguien cuando algo le pasa a un caso: que alguien lo tomó, que se cerró, que cambió de manos."
        paraQue="Hasta que existieron, quien pedía algo no recibía ni un aviso: ni cuando lo tomaban, ni cuando lo cerraban. Lo único que le llegaba era una respuesta que alguien escribía a mano. «Atendió el bot y después de nuestro lado se durmió una semana» fue el problema que esto vino a resolver."
        aQueAfecta={<>Le llegan mensajes <b>a personas reales</b>, por su canal preferido —mail, WhatsApp, Teams— o quedan en su bandeja de la aplicación. No afecta a los agentes ni a cómo trabajan: es sólo comunicación hacia afuera.</>}
        ejemplo={<>El aviso «ya tiene responsable» se dispara cuando el caso entra a un estado que declara <i>implica atendido</i>. Por eso el mismo aviso funciona en un área que llama «en curso» a lo que otra llama «tomado» — se declara con banderas, no con el nombre del estado. Los de <b>autorización</b> son distintos: no miran banderas, salen cuando alguien pide, aprueba o rechaza una autorización del caso.</>}
        ojo={<><b>Prender un aviso le escribe a gente de verdad</b> desde que guardás. Leé el texto antes: es el que va a recibir una persona. Apagarlo no pide confirmación — dejar de mandar nunca necesitó permiso.</>}
      />

      <div className="flex items-start justify-between gap-3">
        <p className="text-[10px] text-zinc-500 max-w-2xl">
          Lo que esta cápsula le avisa a la gente cuando algo le pasa a un caso.
        </p>
        <Badge variant={prendidos ? 'default' : 'secondary'} className="text-[10px] rounded-sm shrink-0">
          {prendidos} prendido{prendidos === 1 ? '' : 's'} de {borrador.length}
        </Badge>
      </div>

      {borrador.map((aviso) => {
        const estados = estadosQueCumplen(aviso, config?.ticket_statuses);
        const tipoDeAviso = typeof aviso.cuando === 'object' ? aviso.cuando?.tipo : aviso.cuando;
        const esDeAutorizacion = DE_UNA_AUTORIZACION.has(tipoDeAviso);
        return (
          <Card key={aviso.id} className={`rounded-sm ${aviso.activo ? 'border-brand-primary/40' : 'border-brand-border'}`}>
            <CardHeader className="py-2 px-3 border-b border-brand-border flex-row items-start justify-between gap-3 space-y-0">
              <div className="flex items-start gap-2">
                {aviso.activo
                  ? <Bell className="w-3.5 h-3.5 text-brand-primary mt-0.5" />
                  : <BellOff className="w-3.5 h-3.5 text-zinc-300 mt-0.5" />}
                <div>
                  <CardTitle className="text-xs font-semibold">
                    {A_QUIEN[aviso.a_quien] || aviso.a_quien}, {cuandoEnProsa(aviso)}
                  </CardTitle>
                  <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{aviso.id}</p>
                  {estados !== null && (
                    <p className="text-[10px] text-zinc-500 mt-1">
                      Hoy, en esta cápsula: {estados.length
                        ? <span className="font-mono">{estados.join(', ')}</span>
                        : <span className="text-amber-700">ningún estado cumple la condición — el aviso no saldría nunca</span>}
                    </p>
                  )}
                </div>
              </div>
              <Switch
                data-testid={`aviso-${aviso.id}`}
                checked={!!aviso.activo}
                onCheckedChange={() => alternar(aviso)}
                className="mt-0.5"
              />
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Asunto</span>
                <Input
                  data-testid={`aviso-asunto-${aviso.id}`}
                  value={aviso.asunto || ''}
                  onChange={(e) => cambiar(aviso.id, 'asunto', e.target.value)}
                  className="rounded-sm text-xs h-7 mt-1"
                />
              </div>
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Texto</span>
                <textarea
                  data-testid={`aviso-texto-${aviso.id}`}
                  value={aviso.plantilla || ''}
                  onChange={(e) => cambiar(aviso.id, 'plantilla', e.target.value)}
                  className="w-full border border-brand-border rounded-sm p-2 text-[11px] h-24 mt-1 font-mono focus:ring-1 focus:ring-brand-primary focus:outline-none resize-y"
                />
              </div>
              {aviso.a_quien === 'personas' && (
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    A quiénes
                  </span>
                  {/* **Se escriben correos y se resuelven contra el directorio.** Un aviso lleva
                      datos del caso: mandarlo a una dirección que nadie reconoció es publicar sin
                      saber a quién. El que no exista no recibe nada y queda en el log. */}
                  <Input
                    data-testid={`aviso-personas-${aviso.id}`}
                    value={(aviso.personas || []).join(', ')}
                    placeholder="director@empresa.com, compras@empresa.com"
                    onChange={(e) => cambiar(
                      aviso.id, 'personas',
                      e.target.value.split(',').map((x) => x.trim()).filter(Boolean),
                    )}
                    className="rounded-sm text-xs h-7 mt-1 font-mono"
                  />
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Separados por coma. Tienen que existir en el directorio: el que no exista no
                    recibe nada.{!(aviso.personas || []).length && (
                      <span className="text-amber-700"> Hoy está vacía, así que no le llega a nadie.</span>
                    )}
                  </p>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {[...VARIABLES, ...(esDeAutorizacion ? VARIABLES_DE_AUTORIZACION : [])].map(([v, que]) => (
                  <span key={v} title={que}
                        className="text-[9px] font-mono border border-brand-border rounded-sm px-1 text-zinc-500">
                    {v}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <div className="flex items-center gap-3">
        <Button
          data-testid="guardar-avisos"
          onClick={() => handleSave('avisos', borrador)}
          disabled={saving || !sucio}
          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Guardar avisos
        </Button>
        {sucio && prendidos > 0 && (
          <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Al guardar, {prendidos === 1 ? 'este aviso empieza' : 'estos avisos empiezan'} a salir de verdad.
          </span>
        )}
      </div>

      <p className="text-[10px] text-zinc-500 max-w-2xl pt-1">
        Un aviso queda registrado <b>aunque no se entregue</b>: si la persona eligió enterarse sólo
        en la aplicación, o no tiene ningún canal cargado, igual lo va a ver en su bandeja y acá va
        a quedar por qué no salió.
      </p>
    </div>
  );
}
